// Remembers, in the browser, how this visitor's SESSION began, and hands that first-touch context to
// every telemetry event (see telemetry.ts). The server — NetRiskScan.Domain.Rules.TrafficAttributionResolver —
// is the only thing that decides a traffic source from it; this file only decides what "the session's
// first touch" is, and keeps it from being lost.
//
// Why the first touch has to be remembered here rather than read off the current page:
//
// * `document.referrer` describes only the page load that is running now. After the visitor clicks a
//   header link (a plain <a href>, so a full page load), it is this site's own previous page. Reading it
//   fresh on every page view is what used to relabel an npm visitor as "www.netriskscan.com".
// * The UTM tags live in the landing URL's query string, and the next navigation — a language switch, a
//   canonical redirect, a router <Navigate> — may not carry them. They have to be captured at the moment
//   the document loads, before anything can move the visitor.
// * npm's package page evidently sends no usable Referer (the reported symptom is an empty referrer — inferred
//   from that, not measured), so for that channel the UTM tag is the only evidence.
//
// A session lasts until 30 minutes pass with no event, or until the visitor arrives from a *different*
// acquisition source (a new UTM campaign, a new external referrer) — which is a new visit, not a
// continuation. It is shared across tabs through localStorage, so a link opened in a new tab (whose own
// referrer is this site) continues the visit instead of starting a "direct" one.
//
// Nothing here may ever throw into the app: storage can be blocked or full, and telemetry is best-effort.

import { requestNonce } from "./requestNonce";

export const SESSION_IDLE_MS = 30 * 60 * 1000;
export const ATTRIBUTION_STORAGE_KEY = "nrs.attribution.v1";

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

/** What the server receives with every event. Raw first-touch inputs, not a verdict. */
export interface AttributionSnapshot {
  sessionId: string;
  /** The session's first URL: origin + path + the UTM parameters, and nothing else from the query. */
  landingUrl: string;
  /** The session's first document.referrer: origin + path only. Empty when there was none. */
  firstReferrer: string;
}

interface StoredSession extends AttributionSnapshot {
  /** Identity of the acquisition signal that started the session — see acquisitionKeyOf. */
  acquisitionKey: string | null;
  startedAt: number;
  lastActivityAt: number;
}

export interface DocumentSignals {
  href: string;
  referrer: string;
}

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** This product's own hostnames — a referrer from one of these is the visitor moving around the site. */
export function isInternalHostname(hostname: string, ownHostname?: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  if (!host) return false;
  if (ownHostname && host === ownHostname.trim().toLowerCase()) return true;
  return (
    host === "netriskscan.com" ||
    host.endsWith(".netriskscan.com") ||
    host === "localhost" ||
    host.endsWith(".localhost") ||
    host === "127.0.0.1" ||
    host === "[::1]" ||
    host === "::1"
  );
}

function parseWebUrl(value: string): URL | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

/** origin + path + only the UTM parameters. Every other query parameter is dropped — `?ip=` is the
 * address the visitor looked up, and none of this is needed to attribute the visit. */
export function landingUrlFor(href: string): string {
  const url = parseWebUrl(href);
  if (!url) return "";

  const kept = new URLSearchParams();
  for (const name of UTM_PARAMS) {
    const value = url.searchParams.get(name)?.trim();
    if (value) kept.set(name, value);
  }

  url.username = "";
  url.password = "";
  url.hash = "";
  url.search = kept.toString();
  return url.toString();
}

/** origin + path: a referrer's query string and fragment can carry tokens, and are not needed. */
export function sanitizeReferrer(referrer: string): string {
  const url = parseWebUrl(referrer);
  return url ? `${url.origin}${url.pathname}` : "";
}

/**
 * What, if anything, this page load says about HOW the visitor arrived. A UTM tag outranks a referrer, and
 * an internal referrer says nothing (null) — exactly the server's own precedence, so the two cannot
 * disagree about whether a page load counts as a new acquisition.
 */
function acquisitionKeyOf(signals: DocumentSignals, ownHostname: string | undefined): string | null {
  const url = parseWebUrl(signals.href);
  const utm = url ? UTM_PARAMS.map((name) => url.searchParams.get(name)?.trim() ?? "") : [];
  if (utm[0]) return `utm|${utm.join("|")}`;

  const referrer = parseWebUrl(signals.referrer);
  if (referrer && !isInternalHostname(referrer.hostname, ownHostname)) return `ref|${sanitizeReferrer(signals.referrer)}`;

  return null;
}

interface AdvanceInput {
  previous: StoredSession | null;
  signals: DocumentSignals;
  /** True only for the page load itself. A client-side route change is not an acquisition. */
  isDocumentLoad: boolean;
  now: number;
  newSessionId: () => string;
  ownHostname?: string;
}

/**
 * The whole session rule, as a pure function.
 *
 * A session continues unless it has been idle for {@link SESSION_IDLE_MS}, or this page load arrived from
 * an acquisition source different from the one that started it. Notably it does NOT restart just because
 * the current page has an internal referrer or no UTM: that is the ordinary shape of every page after the
 * first, and restarting on it is the bug this exists to prevent.
 */
export function advanceSession({ previous, signals, isDocumentLoad, now, newSessionId, ownHostname }: AdvanceInput): StoredSession {
  const key = acquisitionKeyOf(signals, ownHostname);
  const idle = previous === null || now - previous.lastActivityAt > SESSION_IDLE_MS;
  const newAcquisition = isDocumentLoad && key !== null && previous !== null && key !== previous.acquisitionKey;

  if (previous !== null && !idle && !newAcquisition) {
    return { ...previous, lastActivityAt: now };
  }

  return {
    sessionId: newSessionId(),
    landingUrl: landingUrlFor(signals.href),
    firstReferrer: sanitizeReferrer(signals.referrer),
    acquisitionKey: key,
    startedAt: now,
    lastActivityAt: now,
  };
}

function parseStored(raw: string | null): StoredSession | null {
  if (!raw) return null;
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== "object" || value === null) return null;
    const session = value as Partial<StoredSession>;
    if (
      typeof session.sessionId !== "string" ||
      typeof session.landingUrl !== "string" ||
      typeof session.firstReferrer !== "string" ||
      typeof session.startedAt !== "number" ||
      typeof session.lastActivityAt !== "number" ||
      !Number.isFinite(session.lastActivityAt)
    ) {
      return null;
    }
    return {
      sessionId: session.sessionId,
      landingUrl: session.landingUrl,
      firstReferrer: session.firstReferrer,
      acquisitionKey: typeof session.acquisitionKey === "string" ? session.acquisitionKey : null,
      startedAt: session.startedAt,
      lastActivityAt: session.lastActivityAt,
    };
  } catch {
    return null;
  }
}

export interface AttributionTrackerOptions {
  /** null when storage is blocked — the session then lives in memory for this document only. */
  store: KeyValueStore | null;
  now?: () => number;
  newSessionId?: () => string;
  readSignals: () => DocumentSignals;
  ownHostname?: string;
}

export interface AttributionTracker {
  /** Call once when the document loads, before anything can navigate or redirect. Idempotent. */
  init(): AttributionSnapshot;
  /** The session's first-touch context, extending the session's activity. Starts it if need be. */
  snapshot(): AttributionSnapshot;
}

export function createAttributionTracker(options: AttributionTrackerOptions): AttributionTracker {
  const now = options.now ?? Date.now;
  const newSessionId = options.newSessionId ?? requestNonce;
  let memorySession: StoredSession | null = null;
  let initialized = false;

  function load(): StoredSession | null {
    try {
      const stored = parseStored(options.store?.getItem(ATTRIBUTION_STORAGE_KEY) ?? null);
      // Storage is shared with other tabs and is the source of truth when readable; memory is only the
      // fallback for a store that is missing or blocked.
      return stored ?? memorySession;
    } catch {
      return memorySession;
    }
  }

  function save(session: StoredSession): void {
    memorySession = session;
    try {
      options.store?.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Quota or blocked storage: the in-memory copy still carries this document's session.
    }
  }

  function step(isDocumentLoad: boolean): AttributionSnapshot {
    const session = advanceSession({
      previous: load(),
      signals: options.readSignals(),
      isDocumentLoad,
      now: now(),
      newSessionId,
      ownHostname: options.ownHostname,
    });
    save(session);
    return { sessionId: session.sessionId, landingUrl: session.landingUrl, firstReferrer: session.firstReferrer };
  }

  return {
    init() {
      initialized = true;
      return step(true);
    },
    snapshot() {
      // If init() was never called, this is the first look at the document: treat it as the page load,
      // so a UTM tag on the landing URL is still captured rather than missed.
      const isFirstLook = !initialized;
      initialized = true;
      return step(isFirstLook);
    },
  };
}

let singleton: AttributionTracker | null = null;

function browserTracker(): AttributionTracker {
  singleton ??= createAttributionTracker({
    store: safeLocalStorage(),
    readSignals: () => ({ href: window.location.href, referrer: document.referrer }),
    ownHostname: window.location.hostname,
  });
  return singleton;
}

function safeLocalStorage(): KeyValueStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** Captures the landing URL and referrer. Called from main.tsx before the app renders. */
export function initAttribution(): void {
  try {
    browserTracker().init();
  } catch {
    // Attribution is best-effort and must never stop the app from starting.
  }
}

/** Header the website's own API calls carry the session's first touch in — parsed by
 * NetRiskScan.Api.Http.AttributionHeader, so a query log row is attributed to the same channel as the
 * visit's page views instead of to the query request's own Referer (which is always this site). */
export const ATTRIBUTION_HEADER = "X-NRS-Attribution";

/** Form-encoded, so the value is plain ASCII whatever the landing URL's UTM tags contain. */
export function encodeAttributionHeader(snapshot: AttributionSnapshot): string {
  return new URLSearchParams({ sid: snapshot.sessionId, landing: snapshot.landingUrl, ref: snapshot.firstReferrer }).toString();
}

/** The header to attach to a website API call, or an empty object when no snapshot can be produced. */
export function attributionHeaders(): Record<string, string> {
  const snapshot = getAttributionSnapshot();
  return snapshot ? { [ATTRIBUTION_HEADER]: encodeAttributionHeader(snapshot) } : {};
}

/** The current session's first-touch context, or undefined if it cannot be produced. */
export function getAttributionSnapshot(): AttributionSnapshot | undefined {
  try {
    return browserTracker().snapshot();
  } catch {
    return undefined;
  }
}
